import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { groupStyles } from '../../../../utils/SelectStyles';
import Button from '../../../../components/Common/Buttons/Button';

const option = [
    { label: 'Serviceline', value: 'SERVICELINE', placeholder: 'Search account by Serviceline' },
    { label: 'Account Number', value: 'ACCOUNT NUMBER', placeholder: 'Search account by Account Number' },
    { label: 'Name', value: 'NAME', placeholder: 'Search account by Name' },
    { label: 'Address', value: 'ADDRESS', placeholder: 'Search account by Address' },
    { label: 'Equipment', value: 'EQUIPMENT', placeholder: 'Search account by Equipment' },
    { label: 'Company', value: 'COMPANY', placeholder: 'Search account by Company' },
    { label: 'Contact number', value: 'CONTACT NUMBER', placeholder: 'Search account by Contact Number' },
    { label: 'Email', value: 'EMAIL', placeholder: 'Search account by Email' },
    { label: 'Account Id', value: 'ACCOUNT ID', placeholder: 'Search account by Account Id' },
    { label: 'Order Id', value: 'ORDER ID', placeholder: 'Search account by Order Id' },
    { label: 'Service Order Id', value: 'SERVICE ORDER ID', placeholder: 'Search account by Service Order Id' }
];

const SearchBar = ({
                       searchAccounts,
                       changeSearchType,
                       changeTerm,
                       term,
                       searchType,
                       placeholder,
                       getCustomersServedHistory,
                       history,
                       newAccountPermission,
                       searchHistoryPermissions,
                       isLoading,
                       updateFilters,
                       accountStatusOptions,
                       systemLocationFilterOptions,
                       accountTermsFilterOptions,
                       accountTypesFilterOptions,
                       filters
                   }) => {
    const [showFilters, setShowFilters] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);

    useEffect(() => {
        const { accountType, accountStatus, accountTerm, accountLocation } = filters;
        if (accountType || accountStatus || accountTerm || accountLocation) {
            setShowFilters(true);
        }

        if (Array.isArray(accountTypesFilterOptions) && Array.isArray(accountTermsFilterOptions)) {
            setIsLoadingOptions(false);
        }
    }, [filters, accountTypesFilterOptions, accountTermsFilterOptions]);

    // Build select options for accountLocation:
    // - value is the ID (what you send to the backend)
    // - label is the description (what displays)
    const systemLocationSelectOptions = useMemo(() => {
        if (!Array.isArray(systemLocationFilterOptions)) return [];
        return systemLocationFilterOptions.map((loc) => ({
            value: loc.id,
            label: loc.name
        }));
    }, [systemLocationFilterOptions]);

    // Derive currently selected option from the stored ID in filters.accountLocation
    const selectedSystemLocationOption = useMemo(() => {
        if (!filters?.accountLocation) return null;
        return systemLocationSelectOptions.find((o) => o.value === filters.accountLocation) || null;
    }, [filters?.accountLocation, systemLocationSelectOptions]);

    const filterOnChangeHandler = (e, type) => {
        if (e) {
            switch (type) {
                case 'accountStatus':
                    updateFilters('accountStatus', e.value);
                    break;
                case 'accountType':
                    updateFilters('accountType', e.value);
                    break;
                case 'accountTerm':
                    updateFilters('accountTerm', e.value);
                    break;
                case 'accountLocation':
                    // store the ID
                    updateFilters('accountLocation', e.value);
                    break;
                default:
                    break;
            }
        } else {
            // Clears combo-boxes
            switch (type) {
                case 'accountStatus':
                    updateFilters('accountStatus', '');
                    break;
                case 'accountType':
                    updateFilters('accountType', '');
                    break;
                case 'accountTerm':
                    updateFilters('accountTerm', '');
                    break;
                case 'accountLocation':
                    updateFilters('accountLocation', '');
                    break;
                default:
                    break;
            }
        }
    };

    return (
        <div className="cmv-container-dashboard-search">
            <div className="cmv-container-dashboard-filter">
                <div className="container">
                    <div className="form-row">
                        <div className="col-md-3">
                            <Select
                                id="searchType"
                                name="searchType"
                                value={searchType}
                                onChange={changeSearchType}
                                options={option}
                                placeholder="Search"
                                styles={groupStyles}
                                isDisabled={isLoading || isLoadingOptions}
                                isLoading={isLoadingOptions}
                            />
                        </div>

                        <div className="col-md-9">
                            <div className="input-group">
                                <input
                                    type="text"
                                    id="term"
                                    name="term"
                                    className="form-control"
                                    placeholder={placeholder}
                                    onChange={changeTerm}
                                    value={term}
                                    autoComplete="off"
                                    disabled={isLoading}
                                    onKeyPress={(event) => {
                                        if (event.key === 'Enter') {
                                            searchAccounts();
                                        }
                                    }}
                                />

                                <div className="input-group-append">
                                    {searchHistoryPermissions && (
                                        <Button
                                            type="button"
                                            className="btn btn-primary btn-show-cam-search-results"
                                            disabled={isLoading}
                                            onClick={searchAccounts}
                                        >
                                            <i className="fas fa-search" />
                                            <span>&nbsp;Search</span>
                                        </Button>
                                    )}
                                </div>

                                <div className="input-group-append">
                                    <div className="btn-group">
                                        <Button
                                            type="button"
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="btn btn-outline-secondary"
                                        >
                                            {showFilters ? (
                                                <i className="fas fa-solid fa-ban" />
                                            ) : (
                                                <i className="fas fa-solid fa-filter" />
                                            )}
                                            <span>&nbsp;Filters</span>
                                        </Button>

                                        {searchHistoryPermissions && (
                                            <Button
                                                type="button"
                                                onClick={getCustomersServedHistory}
                                                className="btn btn-outline-secondary btn-show-cam-search-history"
                                                disabled={isLoading}
                                            >
                                                <i className="fas fa-history" />
                                                <span>&nbsp;History</span>
                                            </Button>
                                        )}

                                        {newAccountPermission && (
                                            <Button
                                                type="button"
                                                onClick={() => history.push('/create-account')}
                                                className="btn btn-outline-secondary btn-new-account-wizard"
                                            >
                                                <i className="fas fa-plus" />
                                                <span>&nbsp;New Account</span>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {showFilters && (
                            <>
                                <div className="col-md-3" style={{ paddingTop: '15px' }}>
                                    <Select
                                        className="extra-filter-select"
                                        onChange={(e) => filterOnChangeHandler(e, 'accountStatus')}
                                        styles={groupStyles}
                                        options={
                                            Array.isArray(accountStatusOptions)
                                                ? accountStatusOptions.map((opt) => ({
                                                    label: opt,
                                                    value: opt,
                                                    placeholder: opt
                                                }))
                                                : []
                                        }
                                        placeholder="Account Status"
                                        value={
                                            filters.accountStatus?.length
                                                ? { label: filters.accountStatus, value: filters.accountStatus }
                                                : null
                                        }
                                        isClearable={!!filters.accountStatus}
                                        isLoading={isLoadingOptions}
                                        isDisabled={isLoadingOptions}
                                    />
                                </div>

                                <div className="col-md-3" style={{ paddingTop: '15px' }}>
                                    <Select
                                        className="extra-filter-select"
                                        onChange={(e) => filterOnChangeHandler(e, 'accountType')}
                                        styles={groupStyles}
                                        options={
                                            Array.isArray(accountTypesFilterOptions)
                                                ? accountTypesFilterOptions.map((opt) => ({
                                                    label: opt.description,
                                                    placeholder: opt.description,
                                                    value: opt.name
                                                }))
                                                : []
                                        }
                                        placeholder="Account Type"
                                        value={
                                            filters.accountType?.length
                                                ? { label: filters.accountType, value: filters.accountType }
                                                : null
                                        }
                                        isClearable={!!filters.accountType}
                                        isLoading={isLoadingOptions}
                                        isDisabled={isLoadingOptions}
                                    />
                                </div>

                                <div className="col-md-3" style={{ paddingTop: '15px' }}>
                                    <Select
                                        className="extra-filter-select"
                                        onChange={(e) => filterOnChangeHandler(e, 'accountTerm')}
                                        styles={groupStyles}
                                        options={
                                            Array.isArray(accountTermsFilterOptions)
                                                ? accountTermsFilterOptions.map((opt) => ({
                                                    label: opt.description,
                                                    placeholder: opt.description,
                                                    value: opt.name
                                                }))
                                                : []
                                        }
                                        placeholder="Account Terms"
                                        value={
                                            filters.accountTerm?.length
                                                ? { label: filters.accountTerm, value: filters.accountTerm }
                                                : null
                                        }
                                        isClearable={!!filters.accountTerm}
                                        isLoading={isLoadingOptions}
                                        isDisabled={isLoadingOptions}
                                    />
                                </div>

                                <div className="col-md-3" style={{ paddingTop: '15px' }}>
                                    <Select
                                        className="extra-filter-select"
                                        onChange={(e) => filterOnChangeHandler(e, 'accountLocation')}
                                        styles={groupStyles}
                                        options={systemLocationSelectOptions}
                                        placeholder="Account Location"
                                        value={selectedSystemLocationOption}
                                        isClearable={!!filters.accountLocation}
                                        isLoading={isLoadingOptions}
                                        isDisabled={isLoadingOptions}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

SearchBar.propTypes = {
    searchAccounts: PropTypes.func,
    changeSearchType: PropTypes.func,
    changeTerm: PropTypes.func,
    searchType: PropTypes.object,
    term: PropTypes.string,
    placeholder: PropTypes.string,
    getCustomersServedHistory: PropTypes.func,
    history: PropTypes.object,
    newAccountPermission: PropTypes.bool,
    searchHistoryPermissions: PropTypes.bool,
    isLoading: PropTypes.bool,
    updateFilters: PropTypes.func,
    accountStatusOptions: PropTypes.array,
    systemLocationFilterOptions: PropTypes.array,
    accountTermsFilterOptions: PropTypes.array,
    accountTypesFilterOptions: PropTypes.array,
    filters: PropTypes.object
};

export default SearchBar;
